import { useEffect, useState, useMemo } from "react";
import {
  AboutDataType,
  languagesDataType,
} from "../../interfaces/admin.interface";
import { Helmet } from "react-helmet";
import { Button, message, Modal, Upload } from "antd";
import { useFormik } from "formik";
import { LanguageService } from "../../services/languages.service";
import MainButton from "../../components/shared/MainButton/MainButton";
import Spinner from "../../components/shared/Spinner/Spinner";
import { FaEdit, FaRegWindowClose } from "react-icons/fa";
import { AboutService } from "../../services/about.service";
import { UploadOutlined } from "@ant-design/icons";
import RichTextEditor from "react-rte";

const About = () => {
  const [about, setAbout] = useState<AboutDataType | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
  const [langs, setLangs] = useState<languagesDataType[]>([]);
  const [langCode, setLangCode] = useState<string>("uz");
  const [imageList, setImageList] = useState<any[]>([]);

  // Dəyişiklik 1: Həm description, həm də content üçün ayrı editor state-ləri
  const [contentEditorState, setContentEditorState] = useState<{
    [key: string]: any;
  }>({});
  const [descEditorState, setDescEditorState] = useState<{
    [key: string]: any;
  }>({});

  const defaultLang = "uz";

  // 1. Dilləri gətir
  useEffect(() => {
    LanguageService.getAllLanguages().then((response) => {
      const formattedLangs = (response?.data || []).map((l: any) => ({
        ...l,
        code: l.code.toLowerCase(),
      }));
      setLangs(formattedLangs);
      if (formattedLangs.length > 0) {
        setLangCode(defaultLang);
      }
    });
  }, []);

  const fetchAboutData = () => {
    setLoading(true);
    AboutService.getAbout()
      .then((response) => {
        setAbout(response?.data?.data);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchAboutData();
  }, []);

  const displayData = useMemo(() => {
    if (!about) return null;
    const defaultTranslation = about.translations?.find(
      (t) => t.lang_code.toLowerCase() === defaultLang,
    );

    return {
      title: defaultTranslation?.title || about.title || "",
      description: defaultTranslation?.description || about.description || "",
      content: defaultTranslation?.content || about.content || "",
    };
  }, [about]);

  const { handleSubmit, values, resetForm, setFieldValue } = useFormik({
    initialValues: {
      title: "",
      description: "",
      content: "",
      images: [] as string[],
      translations: [] as {
        lang_code: string;
        title: string;
        description: string;
        content: string;
      }[],
    },
    onSubmit: async (formValues) => {
      try {
        const otherTranslations = formValues.translations.map((t) => ({
          ...t,
          lang_code: t.lang_code.toLowerCase(),
        }));

        const uzInArray = {
          lang_code: "uz",
          title: formValues.title,
          description: formValues.description,
          content: formValues.content,
        };

        const finalTranslations = [
          ...otherTranslations.filter((t) => t.lang_code !== "uz"),
          uzInArray,
        ];

        const payload = {
          title: formValues.title,
          description: formValues.description,
          content: formValues.content,
          images: formValues.images,
          translations: finalTranslations,
        };

        const response = await AboutService.updateAbout(payload);

        if (response.status === 200) {
          setIsModalVisible(false);
          resetForm();
          message.success("Haqqımızda uğurla yeniləndi!");
          fetchAboutData();
        } else {
          message.error(response.errorMessage || "Xəta baş verdi.");
        }
      } catch (error) {
        message.error("Nələrsə səhv getdi.");
      }
    },
  });

  const handleTranslationChange = (value: string, field: string) => {
    const currentLang = langCode.toLowerCase();

    if (currentLang === defaultLang) {
      setFieldValue(field, value);
    } else {
      const updatedTranslations = [...values.translations];
      const existingIndex = updatedTranslations.findIndex(
        (t) => t.lang_code.toLowerCase() === currentLang,
      );

      if (existingIndex !== -1) {
        updatedTranslations[existingIndex] = {
          ...updatedTranslations[existingIndex],
          [field]: value,
          lang_code: currentLang,
        };
      } else {
        const existingData = updatedTranslations.find(
          (t) => t.lang_code.toLowerCase() === currentLang,
        );
        updatedTranslations.push({
          lang_code: currentLang,
          title: field === "title" ? value : existingData?.title || "",
          description:
            field === "description" ? value : existingData?.description || "",
          content: field === "content" ? value : existingData?.content || "",
        });
      }
      setFieldValue("translations", updatedTranslations);
    }
  };

  // Dəyişiklik 2: Content Editor Change
  const handleContentEditorChange = (value: any) => {
    setContentEditorState((prev) => ({ ...prev, [langCode]: value }));
    handleTranslationChange(value.toString("html"), "content");
  };

  // Dəyişiklik 3: Description Editor Change
  const handleDescriptionEditorChange = (value: any) => {
    setDescEditorState((prev) => ({ ...prev, [langCode]: value }));
    handleTranslationChange(value.toString("html"), "description");
  };

  const handleLanguageButtonClick = (lang: string) => {
    const code = lang.toLowerCase();
    setLangCode(code);

    // Content Editor yoxdursa yarat
    if (!contentEditorState[code]) {
      setContentEditorState((prev) => ({
        ...prev,
        [code]: RichTextEditor.createEmptyValue(),
      }));
    }

    // Description Editor yoxdursa yarat
    if (!descEditorState[code]) {
      setDescEditorState((prev) => ({
        ...prev,
        [code]: RichTextEditor.createEmptyValue(),
      }));
    }
  };

  const handleEditClick = () => {
    setIsModalVisible(true);
    setLangCode(defaultLang);

    if (!about) return;

    const normalizedTranslations = (about.translations || []).map((t) => ({
      ...t,
      lang_code: t.lang_code.toLowerCase(),
    }));

    const defaultDataInArray = normalizedTranslations.find(
      (t) => t.lang_code === defaultLang,
    );

    setFieldValue(
      "title",
      defaultDataInArray ? defaultDataInArray.title : about.title || "",
    );
    setFieldValue(
      "description",
      defaultDataInArray
        ? defaultDataInArray.description
        : about.description || "",
    );
    setFieldValue(
      "content",
      defaultDataInArray ? defaultDataInArray.content : about.content || "",
    );
    setFieldValue("images", about.images || []);

    const otherTranslations = normalizedTranslations.filter(
      (t) => t.lang_code !== defaultLang,
    );
    setFieldValue("translations", otherTranslations);

    // Dəyişiklik 4: Hər iki editorun initial vəziyyətini hazırlayırıq
    const initialContentState: { [key: string]: any } = {};
    const initialDescState: { [key: string]: any } = {};

    const languagesToProcess = [
      defaultLang,
      ...otherTranslations.map((t) => t.lang_code),
    ];

    languagesToProcess.forEach((code) => {
      // Content Logic
      const content =
        code === defaultLang
          ? defaultDataInArray?.content || about.content
          : otherTranslations.find((t) => t.lang_code === code)?.content;

      initialContentState[code] = content
        ? RichTextEditor.createValueFromString(content, "html")
        : RichTextEditor.createEmptyValue();

      // Description Logic
      const description =
        code === defaultLang
          ? defaultDataInArray?.description || about.description
          : otherTranslations.find((t) => t.lang_code === code)?.description;

      initialDescState[code] = description
        ? RichTextEditor.createValueFromString(description, "html")
        : RichTextEditor.createEmptyValue();
    });

    // Boş qalan dillər üçün
    langs.forEach((l) => {
      if (!initialContentState[l.code])
        initialContentState[l.code] = RichTextEditor.createEmptyValue();
      if (!initialDescState[l.code])
        initialDescState[l.code] = RichTextEditor.createEmptyValue();
    });

    setContentEditorState(initialContentState);
    setDescEditorState(initialDescState);

    setImageList(
      (about.images || []).map((img, index) => ({
        uid: `${index}`,
        name: `img-${index}`,
        url: img,
      })),
    );
  };

  const getBase64 = (file: File, callback: (base64: string) => void) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => callback(reader.result as string);
  };

  const handleFileUpload = (file: File) => {
    getBase64(file, (base64: string) => {
      setImageList((prevList) => {
        const newList = [
          ...prevList,
          { uid: `${Date.now()}`, name: file.name, url: base64 },
        ];
        setFieldValue(
          "images",
          newList.map((img) => img.url),
        );
        return newList;
      });
    });
    return false;
  };

  const handleImageDelete = (uid: string) => {
    const updatedImageList = imageList.filter((img) => img.uid !== uid);
    setImageList(updatedImageList);
    setFieldValue(
      "images",
      updatedImageList.map((img) => img.url),
    );
  };

  const getInputValue = (field: "title") => {
    if (langCode === defaultLang) return values[field];
    const t = values.translations.find((item) => item.lang_code === langCode);
    return t ? t[field] : "";
  };

  return (
    <div id="pages">
      <Helmet>
        <title>Orhan Door | Haqqımızda</title>
      </Helmet>

      {loading ? (
        <Spinner />
      ) : (
        <div>
          <div className="page_head">
            <h3 className="page_title">Haqqımızda</h3>
          </div>
          <div className="pages_components">
            <MainButton onClick={handleEditClick}>
              <FaEdit size={20} />
            </MainButton>
            <div className="pages_table">
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "24px",
                  margin: "30px 0",
                }}
              >
                {about?.images?.map((image, index) => (
                  <img
                    key={index}
                    width="500px"
                    height="400px"
                    style={{ objectFit: "cover", borderRadius: "12px" }}
                    src={image}
                    alt=""
                  />
                ))}
              </div>
              <div style={{ fontSize: "24px", fontWeight: "bold" }}>
                {displayData?.title}
              </div>

              {/* Dəyişiklik 5: View hissəsində description-ı HTML kimi göstəririk */}
              <div
                style={{ margin: "16px 0" }}
                dangerouslySetInnerHTML={{
                  __html: displayData?.description || "",
                }}
              />

              <div
                dangerouslySetInnerHTML={{ __html: displayData?.content || "" }}
              />
            </div>
          </div>
        </div>
      )}

      <Modal
        title="Haqqımızda səhifəsini yeniləyin"
        onCancel={() => setIsModalVisible(false)}
        open={isModalVisible}
        footer={null}
        width="100%"
        centered
      >
        <form className="modal_form" onSubmit={handleSubmit}>
          <div className="langs">
            {langs.map((lang, index) => (
              <button
                key={index}
                className={lang.code === langCode ? "active_lang_btn" : ""}
                type="button"
                onClick={() => handleLanguageButtonClick(lang.code)}
              >
                {lang.code === "uz" ? lang.code : lang.code.toUpperCase()}
              </button>
            ))}
          </div>

          <div className="form_input">
            <label className="label">
              Haqqımızda başlıq (
              {langCode === "uz" ? "uz" : langCode.toUpperCase()})
            </label>
            <div className="input_wrapper">
              <input
                value={getInputValue("title")}
                onChange={(e) =>
                  handleTranslationChange(e.target.value, "title")
                }
              />
              <span className="focus_border"></span>
            </div>
          </div>

          {/* Dəyişiklik 6: Description Input -> RichTextEditor */}
          <div className="form_input">
            <label className="ck_label">
              Haqqımızda təsvir (
              {langCode === "uz" ? "uz" : langCode.toUpperCase()})
            </label>
            <RichTextEditor
              value={
                descEditorState[langCode] || RichTextEditor.createEmptyValue()
              }
              onChange={handleDescriptionEditorChange}
            />
          </div>

          <div className="form_input">
            <label className="ck_label">Haqqımızda kontenti</label>
            <RichTextEditor
              value={
                contentEditorState[langCode] ||
                RichTextEditor.createEmptyValue()
              }
              onChange={handleContentEditorChange}
            />
          </div>

          <div className="form_input">
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: "24px",
                margin: "20px 0",
              }}
            >
              {imageList.map((img) => (
                <div
                  key={img.uid}
                  style={{ display: "flex", alignItems: "flex-end" }}
                >
                  <img
                    height="400px"
                    src={img.url}
                    style={{ objectFit: "cover", borderRadius: "12px" }}
                    alt=""
                  />
                  <FaRegWindowClose
                    onClick={() => handleImageDelete(img.uid)}
                    style={{
                      fontSize: "25px",
                      color: "red",
                      cursor: "pointer",
                      marginLeft: "8px",
                    }}
                  />
                </div>
              ))}
            </div>
            <Upload
              beforeUpload={handleFileUpload}
              showUploadList={false}
              multiple
            >
              <Button icon={<UploadOutlined />}>Şəkilləri seçin</Button>
            </Upload>
          </div>

          <MainButton full type="submit" className="form_add_btn">
            Redaktə edin
          </MainButton>
        </form>
      </Modal>
    </div>
  );
};

export default About;
